<template>
  <span
    v-if="proxyName"
    :class="[
      'ui-icon',
      proxyTheme,
    ]"
    :data-name="name"
  >{{ proxyName }}</span>
</template>

<script lang="ts">
import { includes } from '~/utils/lib/core/prelude'
import type { ICON_THEME } from '~/utils/lib/layout/ui'

const ICON_LIST_WITH_OUTLINED_THEME_ISSUE = [
  'error',
]

/**
 * @link https://fonts.google.com/icons?icon.set=Material+Icons
 */
export const IconProps = {
  name: {
    type: String,
    required: true
  },
  theme: {
    type: String as PropType<ICON_THEME>,
    required: false,
    default: 'outlined' satisfies ICON_THEME
  },
} as const
  // @link https://github.com/vuejs/composition-api/issues/106

export default defineComponent({
  props: IconProps,
  data() {
    return {
      proxyTheme: '',
      proxyName: '',
    }
  },
  watch: {
    theme: {
      immediate: true,
      handler() { this.setProxyProperties() }
    },
    name: {
      immediate: true,
      handler() { this.setProxyProperties() }
    },
  },
  methods: {
    setProxyProperties() {
      let name = this.name
      let theme = this.theme as string
      // Some icons have the same display style whatever the theme.
      // These icons don't rely on the theme "outlined", but rather use the
      // "filled" theme with a name suffix (ie. "error" as an outlined icon will
      // be "error_outline" with the default filled theme)
      try {
        checkOutlinedThemeIssue({ theme, name })

      } catch (e) {
        name = `${name}_outline`
        theme = 'filled'
      }
      // Only comprehensive hint to how material icon themes works...[1]
      // @link https://stackoverflow.com/questions/50303454/how-to-use-the-new-material-design-icon-themes-outlined-rounded-two-tone-and#answer-51415409 [1]
      theme = getThemeClassName({ theme })

      this.proxyName = name
      this.proxyTheme = theme
    }
  }
})
/** @throws {string} */
function checkOutlinedThemeIssue({ theme, name }: { theme: string, name: string }):void {
  if (theme !== 'outlined')
    // issue only occuring with "outlined" theme
    return

  if (includes(name, ICON_LIST_WITH_OUTLINED_THEME_ISSUE))
    throw 'this icon need to be suffixed with _outline'
}

function getThemeClassName({ theme }: { theme: string }) {
  // @XXX VueJS props with empty string will be truthy (hence will not be
  //      set as "filled")
  return theme === 'filled' || theme === '' ?
    `material-icons` :
    `material-icons-${theme}`
}
</script>

<style lang="sass">
/**
 *  Based on Google guideline
 *  @link https://google.github.io/material-design-icons/#icon-font-for-the-web
 */
.ui-icon
  font-weight: normal
  font-style: normal
  display: inline-block
  line-height: 1
  text-transform: none
  letter-spacing: normal
  word-wrap: normal
  white-space: nowrap
  direction: ltr
  -webkit-font-smoothing: antialiased
  text-rendering: optimizeLegibility
  -moz-osx-font-smoothing: grayscale
  font-feature-settings: 'liga'

  position: relative // will allow z-index positioning
  // font-size: inherit // (?) would `$icon-default-size` be better?
  vertical-align: middle // seems the best fit

</style>
